

import { AfterViewInit, Component, ElementRef, ViewChild, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MentionDirective } from 'angular-mentions';
import * as _ from 'lodash';
import { WizardFlowDecisionContext } from '../../models/wizard-flow-decision-context.model';
import { ContextExtractor } from '../../services/context-extractor';
import { WizardFlowServiceBase } from '../../services/wizard/wizard-flow-service.base';

@Component({
    selector: 'expression-editor',
    templateUrl: 'expression-editor.component.html',
    styleUrls: ['expression-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ExpressionEditorComponent),
            multi: true
        }
    ],
    standalone: false
})

export class ExpressionEditorComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @ViewChild(MentionDirective)
  private _mentionDirective?: MentionDirective;

  @ViewChild('expressionTextarea')
  private _expressionTextarea?: ElementRef<HTMLTextAreaElement>;

  @Input()
  name: string;

  expression: string = "";

  triggerCharacter = '';

  private _lastWord: string = "";

  private _currentListOfChoices: string[] = [];

  private _currentObject: any = undefined;

  private _newWordStarted: boolean = false;

  private _searchText: string = "";

  private _isMenuVisible: boolean = false;
  private readonly _manualOpenPlaceholderChar = '\u200B';

  onChange: any = () => { };

  onTouch: any = () => { };

  _decisionContext: WizardFlowDecisionContext | undefined;
  private readonly _mentionTriggerChar = '@';

  // angular-mentions data
  mentionItems: string[] = [];
  mentionConfig: any = {
    triggerChar: '@',
    labelKey: 'label',
    mentionSelect: (item: any) => item?.label ?? item
  };

  keyboardShortcut = (event: KeyboardEvent) => {

    if (this._isMenuVisible) {
      return false;
    }

    this._searchText = event.key;

    if ((event.keyCode === 32 || event.code === '32') && event.ctrlKey) {
      return true;
    }

    const key = event.key;

    // Nothing is selected, we start from scratch at the root
    if (this._lastWord === '') {
      const contextProperties = Object.getOwnPropertyNames(this._decisionContext).filter((c: any) => isNaN(c));;
      this._currentListOfChoices = contextProperties;
    } else {
      if (event.key == '.') {
        this.triggerCharacter = '.';
        return true;
      }
    }

    const found = this._currentListOfChoices.find(p => p.includes(key));
    if (found) {
      return true;
    }
    return false;
  }

  constructor(private readonly _wizardFlowService: WizardFlowServiceBase) {
    this._decisionContext = ContextExtractor.prepareDecisionContext(this._wizardFlowService.context);
  }

  get value(): string {
    return this.expression;
  }

  writeValue(expression: any): void {
    this.expression = expression;
    this.onChange(this.value);
  }

  registerOnChange(fn: (rating: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  ngAfterViewInit(): void {
    //$("#expression").focus();
  }

  ngOnInit() {
    this._currentObject = this._decisionContext;
    // Initialize root-level choices for Ctrl+Space
    this._currentListOfChoices = this.getPossibleChoicesFromObject(this._currentObject);
    this.mentionItems = this._currentListOfChoices;
  }

  onMenuShown = () => {
    this._searchText = '';
    this._isMenuVisible = true;
  }

  onKeyDown(event: any) {
    if (event.ctrlKey && event.code === 'Space') {
      event.preventDefault();
      this.refreshChoicesForCurrentToken();
      this.openMentionMenuWithoutLeavingTriggerChar();
      return;
    }

    if (this._isMenuVisible && (event.code === 'ArrowDown' || event.code === 'ArrowUp' || event.code === 'Enter' || event.code === 'Escape' || event.code === 'Tab')) {
      return;
    }

    if (!event.ctrlKey && !event.metaKey && !event.altKey && event.key === '.') {
      setTimeout(() => {
        this.refreshChoicesForCurrentToken();
        if (this._currentListOfChoices.length > 0) {
          this.openMentionMenuWithoutLeavingTriggerChar();
        }
      }, 0);
    }

    if (event.code === 'Enter') {
      event.preventDefault();
      return;
    }
    if (event.code === 'Space') {
      this._newWordStarted = true;
      this._currentObject = this._decisionContext;
      this._currentListOfChoices = this.getPossibleChoicesFromObject(this._currentObject);
      this.mentionItems = this._currentListOfChoices;
    }
  }

  onMenuHidden = () => {
    this._isMenuVisible = false;
    this.removeManualOpenPlaceholder();
  }

  // angular-mentions hook: provide items based on current context
  onMentionSearch = (term: string) => {
    const list = this.findChoices((term || '').replace(this._mentionTriggerChar, ''));
    this.mentionItems = list;
  }

  // Handle selection: switch to dot trigger after root selection
  onItemSelected = () => {
    const textarea = this._expressionTextarea?.nativeElement ?? null;
    if (!textarea) return;
    setTimeout(() => {
      this.expression = this.getSanitizedExpression(textarea.value);
      // Update context based on what was selected
      const lastWord = this.expression.split(' ').pop() || '';
      this._lastWord = lastWord;
      this.decideOnCurrentObjectContext(lastWord);
      // Update mentionItems with the new context's properties
      this.mentionItems = this._currentListOfChoices.slice();
      this.onExpressionChanged();
    }, 0);
  }

  findChoices = (searchText: string) => {
    if (searchText.length === 0 && this._searchText.length > 0 && this._searchText != '.') {
      searchText = this._searchText;
    }
    return this._currentListOfChoices.filter(item =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  }

  getChoiceLabel = (choice: string) => {
    if (this._newWordStarted || !this.triggerCharacter) {
      this._newWordStarted = false;
      this.triggerCharacter = '.';
      return choice;
    }
    return '.' + choice;
  }

  onExpressionChanged = () => {
    const sanitizedExpression = this.getSanitizedExpression(this.expression);
    if (!this._isMenuVisible && sanitizedExpression !== this.expression) {
      this.expression = sanitizedExpression;
      const textarea = this._expressionTextarea?.nativeElement;
      if (textarea) {
        const caretPos = textarea.selectionStart ?? sanitizedExpression.length;
        textarea.value = sanitizedExpression;
        textarea.setSelectionRange(Math.min(caretPos, sanitizedExpression.length), Math.min(caretPos, sanitizedExpression.length));
      }
    }
    this.onChange(sanitizedExpression);
    const lastWord = sanitizedExpression.split(" ").splice(-1)[0];
    this._lastWord = lastWord;
    this.decideOnCurrentObjectContext(lastWord);
    const lastCharacter = sanitizedExpression.charAt(sanitizedExpression.length - 1);
    if (lastCharacter === ' ' || sanitizedExpression.length === 0) {
      this._newWordStarted = true;
      this.triggerCharacter = '';
    }
  }

  decideOnCurrentObjectContext = (lastWord: string) => {
    let currentObject: any = this._decisionContext;
    const tokens = lastWord.split('.');
    tokens.forEach((token: string) => {
      if (token.length > 0)
        currentObject = currentObject[token];
    });
    if (currentObject != null) {
      if (typeof currentObject === 'object') {
        this._currentObject = currentObject;
        this._currentListOfChoices = Object.getOwnPropertyNames(this._currentObject).filter((c: any) => isNaN(c));
      } else {
        this._currentListOfChoices = [];
      }
    } else {
      if (!this._currentObject) {
        this._currentObject = this._decisionContext;
        this._currentListOfChoices = this.getPossibleChoicesFromObject(this._currentObject);
      }
    }
  }

  getPossibleChoicesFromObject = (o: any): string[] => {
    const choices = Object.getOwnPropertyNames(this._currentObject).filter((c: any) => isNaN(c)).map((c: any) => {
      if (typeof c === 'function') {
        return c + '()';
      }
      return c;
    });
    return choices;
  }

  private refreshChoicesForCurrentToken = () => {
    const lastWord = this.getSanitizedExpression(this.expression).split(" ").splice(-1)[0];
    this._lastWord = lastWord;
    this.decideOnCurrentObjectContext(lastWord);
    this.mentionItems = this._currentListOfChoices.slice();
  }

  private openMentionMenuWithoutLeavingTriggerChar = () => {
    const textarea = this._expressionTextarea?.nativeElement;
    if (!textarea || !this._mentionDirective) {
      return;
    }

    const caretPos = textarea.selectionStart ?? textarea.value.length;
    this._mentionDirective.startSearch(this._mentionTriggerChar, textarea as any);

    // startSearch() always inserts the trigger character. Replace it with an
    // invisible placeholder so the directive's internal search state stays valid
    // (arrow keys/click selection rely on that character range existing).
    const value = textarea.value ?? '';
    if (value.charAt(caretPos) === this._mentionTriggerChar) {
      const updatedValue = value.slice(0, caretPos) + this._manualOpenPlaceholderChar + value.slice(caretPos + 1);
      textarea.value = updatedValue;
      textarea.setSelectionRange(caretPos + 1, caretPos + 1);
      this.expression = updatedValue;
    }
  }

  private removeManualOpenPlaceholder = () => {
    const textarea = this._expressionTextarea?.nativeElement;
    if (!textarea) {
      this.expression = this.getSanitizedExpression(this.expression);
      return;
    }

    const value = textarea.value ?? '';
    if (!value.includes(this._manualOpenPlaceholderChar)) {
      return;
    }

    const caretPos = textarea.selectionStart ?? value.length;
    const beforeCaret = value.slice(0, caretPos);
    const removedBeforeCaretCount = (beforeCaret.match(/\u200B/g) || []).length;
    const sanitized = this.getSanitizedExpression(value);
    const nextCaret = Math.max(0, caretPos - removedBeforeCaretCount);

    textarea.value = sanitized;
    textarea.setSelectionRange(nextCaret, nextCaret);
    this.expression = sanitized;
  }

  private getSanitizedExpression = (value: string): string => {
    return (value || '').split(this._manualOpenPlaceholderChar).join('');
  }

}
